import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Pipeline from './pages/Pipeline';
import ClinicView from './pages/ClinicView';
import TodaysActions from './pages/TodaysActions';
import Inquiries from './pages/Inquiries';
import Feedback from './pages/Feedback';
import Setup from './pages/Setup';
import Users from './pages/Users';
import PendingNotes from './pages/PendingNotes';
import ImportPage from './pages/Import';
import ODDigest from './pages/ODDigest';
import Performance from './pages/Performance';
import ClinicianManager from './pages/ClinicianManager';
import ExecutiveBI from './pages/ExecutiveBI';
import RateEditor from './pages/RateEditor';
import HelpGuide from './pages/HelpGuide';
import Vault from './pages/Vault';
import LudoPipeline from './pages/LudoPipeline';
import LudoSchedule from './pages/LudoSchedule';
import LudoDashboard from './pages/LudoDashboard';
import VaultOnboard from './pages/VaultOnboard';
import Underpayment from './pages/Underpayment';
import DeniedClaims from './pages/DeniedClaims';
import Payroll from './pages/Payroll';
import ReferralLinks from './pages/ReferralLinks';
import ReferralForm from './pages/ReferralForm';
import LowDeductibleReport from './pages/LowDeductibleReport';
import MonthlyClinicReport from './pages/MonthlyClinicReport';
import HRCommandCenter from './pages/HRCommandCenter';
import Layout from './components/Layout';
import FloatingChat from './components/FloatingChat';
import { DrilldownProvider } from './components/DrillDownDrawer';
import './App.css';

const ADMIN_ROLES = ['super_user', 'admin'];
const PAYROLL_ROLES = ['super_user', 'hr_payroll'];
// HR Command Center — Workforce Overview, Clinic Staffing, Hiring Pipeline,
// Retention & Attrition. Same role set as Payroll (super_user + hr_payroll).
// Office Directors are explicitly blocked.
const HR_ROLES = ['super_user', 'hr_payroll'];
// Iter 161 role matrix: HR/Payroll gets the same ops surface as admin
// (dashboards, pipeline, inquiries, performance, clinicians, setup,
// digest, pending notes, data import) EXCEPT Executive BI, Vault, and
// Underpayment. Super_admin has been consolidated into admin.
const OPS_ADMIN_ROLES = ['super_user', 'admin', 'hr_payroll'];
// Data Import Center is also accessible by the service-account role that
// external AI agents use for automated SP report uploads.
const IMPORT_ROLES = ['super_user', 'data_import'];
const PERFORMANCE_ROLES = ['super_user', 'admin', 'hr_payroll', 'office_director'];
const INQUIRY_ROLES = ['super_user', 'admin'];
const PENDING_NOTES_ROLES = ['super_user', 'admin', 'hr_payroll', 'office_director', 'read_only'];
const CLINICIAN_ROSTER_ROLES = ['super_user', 'admin', 'hr_payroll', 'office_director', 'read_only'];
const DIGEST_ROLES = ['super_user', 'admin', 'hr_payroll', 'office_director'];
// Monthly Clinic Report is intentionally OD-focused with super_user override.
// admin + hr_payroll can also generate for org-wide operations reviews.
const MONTHLY_REPORT_ROLES = ['super_user', 'admin', 'hr_payroll', 'office_director'];
// Ops surface for the referral-links admin (all non-pending, non-read_only).
const REFERRAL_ADMIN_ROLES = ['super_user', 'admin', 'hr_payroll', 'office_director'];
const SUPER_ONLY = ['super_user'];

function Protected({ children, require, chromeless = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#685B72]" data-testid="loading-screen">Loading Thrive Ops Hub…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Data-Import service-account role: only /import is allowed. Any other
  // route redirects home (which itself redirects to /import for this role).
  if (user.role === 'data_import') {
    const path = window.location.pathname;
    if (!path.startsWith('/import')) return <Navigate to="/import" replace />;
    if (chromeless) return children;
    return <Layout>{children}</Layout>;
  }
  if (require && !require.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (chromeless) return children;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent Auth callback synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/vault/onboard/:token" element={<VaultOnboard />} />
      <Route path="/refer/:token" element={<ReferralForm />} />
      <Route path="/referral-links" element={<Protected require={REFERRAL_ADMIN_ROLES}><ReferralLinks /></Protected>} />
      <Route path="/vault" element={<Protected require={ADMIN_ROLES}><Vault /></Protected>} />
      <Route path="/ludo/pipeline" element={<Protected><LudoPipeline /></Protected>} />
      <Route path="/ludo/dashboard" element={<Protected><LudoDashboard /></Protected>} />
      <Route path="/ludo/schedule" element={<Protected><LudoSchedule /></Protected>} />
      <Route path="/underpayment" element={<Protected require={ADMIN_ROLES}><Underpayment /></Protected>} />
      <Route path="/denied-claims" element={<Protected require={ADMIN_ROLES}><DeniedClaims /></Protected>} />
      <Route path="/payroll" element={<Protected require={PAYROLL_ROLES}><Payroll /></Protected>} />
      <Route path="/payroll/:runId" element={<Protected require={PAYROLL_ROLES}><Payroll /></Protected>} />
      <Route path="/hr" element={<Protected require={HR_ROLES}><HRCommandCenter /></Protected>} />
      <Route path="/hr/:tab" element={<Protected require={HR_ROLES}><HRCommandCenter /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
      <Route path="/clinics/:clinicId" element={<Protected><ClinicView /></Protected>} />
      <Route path="/today" element={<Protected><TodaysActions /></Protected>} />
      <Route path="/pending-notes" element={<Protected require={PENDING_NOTES_ROLES}><PendingNotes /></Protected>} />
      <Route path="/import" element={<Protected require={IMPORT_ROLES}><ImportPage /></Protected>} />
      <Route path="/od-digest" element={<Protected require={DIGEST_ROLES}><ODDigest /></Protected>} />
      <Route path="/monthly-report" element={<Protected require={MONTHLY_REPORT_ROLES} chromeless><MonthlyClinicReport /></Protected>} />
      <Route path="/performance" element={<Protected require={PERFORMANCE_ROLES}><Performance /></Protected>} />
      <Route path="/low-deductible" element={<Protected require={PERFORMANCE_ROLES}><LowDeductibleReport /></Protected>} />
      <Route path="/clinicians" element={<Protected require={CLINICIAN_ROSTER_ROLES}><ClinicianManager /></Protected>} />
      <Route path="/executive-bi" element={<Protected require={SUPER_ONLY}><ExecutiveBI /></Protected>} />
      <Route path="/executive-bi/rates" element={<Protected require={SUPER_ONLY}><RateEditor /></Protected>} />
      <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
      <Route path="/help" element={<Protected><HelpGuide /></Protected>} />
      <Route path="/inquiries" element={<Protected require={INQUIRY_ROLES}><Inquiries /></Protected>} />
      <Route path="/setup" element={<Protected require={OPS_ADMIN_ROLES}><Setup /></Protected>} />
      <Route path="/users" element={<Protected require={SUPER_ONLY}><Users /></Protected>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DrilldownProvider>
            <AppRouter />
            <FloatingChat />
            <Toaster position="top-right" richColors />
          </DrilldownProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
